import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Button, 
  Card, 
  CardContent, 
  CardActions,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip
} from '@mui/material';
import { 
  Link as LinkIcon,
  DirectionsCar as CarIcon,
  Chat as ChatIcon,
  Search as SearchIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { Link, useNavigate } from 'react-router-dom';
import ChatList from '../components/chat/ChatList';
import AppLayout from '../components/layout/AppLayout';
import { getUserChats } from '../services/chatService';
import { ChatListItem } from '../types/chat';
import { useAuth } from '../contexts/AuthContext';
import LoadingScreen from '../components/ui/LoadingScreen';

const HomePage: React.FC = () => {
  const { userProfile, refreshUserProfile } = useAuth();
  const navigate = useNavigate();
  const [recentChats, setRecentChats] = useState<ChatListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);
  
  // State for the input options
  const [carListingUrl, setCarListingUrl] = useState('');
  const [vinNumber, setVinNumber] = useState('');
  const [dealerName, setDealerName] = useState('');

  useEffect(() => {
    const fetchRecentChats = async () => {
      try {
        setLoading(true);
        // Add a small delay for initial load to show loading messages
        if (initialLoad) {
          await new Promise(resolve => setTimeout(resolve, 1000));
          setInitialLoad(false);
        }
        const chats = await getUserChats();
        setRecentChats(chats.slice(0, 3)); // Get the 3 most recent chats
      } catch (error) {
        console.error('Error fetching chats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchRecentChats();
  }, [initialLoad]);

  // Refresh user profile if coin balance is 0 (might be a new user)
  useEffect(() => {
    if (userProfile && userProfile.coinBalance === 0) {
      const timer = setTimeout(() => {
        refreshUserProfile();
      }, 1000); // Wait 1 second then refresh
      
      return () => clearTimeout(timer);
    }
  }, [userProfile, refreshUserProfile]);

  // Handlers for the new options
  const handleCarListingAnalysis = () => {
    if (!carListingUrl.trim()) return;
    // TODO: Navigate to new chat with car listing URL
    navigate('/new-chat', { 
      state: { 
        initialMessage: `Please analyze this car listing: ${carListingUrl}`,
        type: 'car_listing'
      }
    });
  };

  const handleVinAnalysis = () => {
    if (!vinNumber.trim()) return;
    let message = `Please analyze this vehicle with VIN: ${vinNumber}`;
    if (dealerName.trim()) {
      message += ` from dealer: ${dealerName}`;
    }
    // TODO: Navigate to new chat with VIN analysis
    navigate('/new-chat', { 
      state: { 
        initialMessage: message,
        type: 'vin_analysis'
      }
    });
  };

  const handleGeneralChat = () => {
    navigate('/new-chat');
  };

  // Show loading screen while fetching initial data
  if (loading && initialLoad) {
    return <LoadingScreen message="Setting up your dashboard..." />;
  }

  return (
    <AppLayout>
      <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 2, sm: 3 } }}>
        {/* Welcome section */}
        <Paper 
          elevation={0} 
          sx={{ 
            p: { xs: 3, sm: 4 }, 
            mb: 4, 
            borderRadius: 2, 
            bgcolor: 'background.paper',
            backgroundImage: 'linear-gradient(to right, rgba(79, 70, 229, 0.1), rgba(16, 185, 129, 0.1))',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: '1.75rem', sm: '2.125rem' } }}>
            Welcome, {userProfile?.nickname || 'User'}!
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            CarConsul is here to help you find the perfect vehicle. Choose how you'd like to get started:
          </Typography>
        </Paper>

        {/* Main options cards */}
        <Box 
          sx={{ 
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 3,
            mb: 4
          }}
        >
          {/* Option 1: Car Listing Analysis */}
          <Box sx={{ flex: 1 }}>
            <Card 
              elevation={2}
              sx={{ 
                height: '100%',
                transition: 'all 0.3s ease-in-out',
                border: '2px solid',
                borderColor: 'warning.main',
                bgcolor: 'warning.50',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: (theme) => theme.shadows[8],
                  borderColor: 'warning.dark',
                }
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <LinkIcon sx={{ fontSize: 32, color: 'warning.main', mr: 1 }} />
                  <Box>
                    <Typography variant="h6" fontWeight="bold">
                      Analyze Car Listing
                    </Typography>
                    <Typography variant="caption" color="warning.main" fontWeight="bold">
                      (most accurate)
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Paste a car listing URL and get detailed analysis, pricing insights, and recommendations.
                </Typography>
                <TextField
                  fullWidth
                  placeholder="https://example.com/car-listing"
                  variant="outlined"
                  size="small"
                  value={carListingUrl}
                  onChange={(e) => setCarListingUrl(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LinkIcon sx={{ color: 'text.secondary' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mb: 2 }}
                />
              </CardContent>
              <CardActions sx={{ p: 3, pt: 0 }}>
                <Button 
                  variant="contained" 
                  fullWidth
                  disabled={!carListingUrl.trim()}
                  onClick={handleCarListingAnalysis}
                  startIcon={<SearchIcon />}
                  color="warning"
                >
                  Analyze Listing
                </Button>
              </CardActions>
            </Card>
          </Box>

          {/* Option 2: VIN Analysis */}
          <Box sx={{ flex: 1 }}>
            <Card 
              elevation={2}
              sx={{ 
                height: '100%',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: (theme) => theme.shadows[8],
                }
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <CarIcon sx={{ fontSize: 32, color: 'success.main', mr: 1 }} />
                  <Typography variant="h6" fontWeight="bold">
                    Check by VIN
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Enter a VIN number to get comprehensive vehicle history and analysis.
                </Typography>
                <TextField
                  fullWidth
                  placeholder="VIN Number (17 characters)"
                  variant="outlined"
                  size="small"
                  value={vinNumber}
                  onChange={(e) => setVinNumber(e.target.value.toUpperCase())}
                  inputProps={{ maxLength: 17 }}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  placeholder="Dealer Name (optional)"
                  variant="outlined"
                  size="small"
                  value={dealerName}
                  onChange={(e) => setDealerName(e.target.value)}
                />
              </CardContent>
              <CardActions sx={{ p: 3, pt: 2 }}>
                <Button 
                  variant="contained" 
                  fullWidth
                  disabled={!vinNumber.trim() || vinNumber.length !== 17}
                  onClick={handleVinAnalysis}
                  startIcon={<SearchIcon />}
                  color="success"
                >
                  Check Vehicle
                </Button>
              </CardActions>
            </Card>
          </Box>

          {/* Option 3: General Chat */}
          <Box sx={{ flex: 1 }}>
            <Card 
              elevation={2}
              sx={{ 
                height: '100%',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: (theme) => theme.shadows[8],
                }
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <ChatIcon sx={{ fontSize: 32, color: 'info.main', mr: 1 }} />
                  <Typography variant="h6" fontWeight="bold">
                    General Chat
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Start a conversation to ask questions about cars, get buying advice, or discuss options.
                </Typography>
                <Box sx={{ height: '84px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography variant="body2" color="text.secondary" textAlign="center">
                    Ask me anything about cars,<br />
                    pricing, or buying advice!
                  </Typography>
                </Box>
              </CardContent>
              <CardActions sx={{ p: 3, pt: 0 }}>
                <Button 
                  variant="contained" 
                  fullWidth
                  onClick={handleGeneralChat}
                  startIcon={<ChatIcon />}
                  color="info"
                >
                  Start Chat
                </Button>
              </CardActions>
            </Card>
          </Box>
        </Box>

        {/* Recent chats section */}
        <Paper 
          elevation={0} 
          sx={{ 
            p: 3, 
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper'
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" fontWeight="bold">
              Recent Conversations
            </Typography>
            <Tooltip title="Your recent chat history">
              <IconButton size="small">
                <InfoIcon />
              </IconButton>
            </Tooltip>
          </Box>
          
          {recentChats.length > 0 ? (
            <>
              <ChatList />
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                <Button component={Link} to="/chats" variant="outlined">
                  View All Chats
                </Button>
              </Box>
            </>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography color="text.secondary" paragraph>
                No recent conversations yet.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Start analyzing cars or chatting to see your history here!
              </Typography>
            </Box>
          )}
        </Paper>
      </Box>
    </AppLayout>
  );
};

export default HomePage;
